import React from 'react';
import { initReactQueryAuth } from 'react-query-auth';
import type { TNavigation, TUserData } from 'src/types';
import type { AxiosError } from 'axios';
import { DEFAULT_TIME_ZONE } from 'src/constants';
import { PageLoader } from 'src/components/PageLoader';
import get from 'lodash/get';
import intersectionWith from 'lodash/intersectionWith';
import { authClient, bffClient, httpClient } from './HttpClients';

export type TAuthData = {
    navigation: TNavigation;
    profile: TUserData;
};

export const ErrorComponent = ({ error }: { error: unknown }) => {
    if (typeof window !== 'undefined' && (error as AxiosError)?.response?.status === 401) {
        return <PageLoader withMenu />;
    }

    return (
        <div className="d-flex justify-content-center align-items-center flex-column">
            <h1 className="mt-5">Oops! Something bad happened.</h1>
            <p>Server {(error as Error)?.message ?? 'Responded with error.'}</p>
        </div>
    );
};

export const loaderComponent = () => <PageLoader withMenu />;

export const getAppData = async (): Promise<TAuthData | undefined> => {
    const [{ data: navigation }, { data: profile }, timezone] = await Promise.all([
        bffClient.get<TNavigation>('/navigation'),
        bffClient.get<TUserData>('/user'),
        httpClient.get<{ data: string }>('/preferences/account/tz'),
    ]);
    return {
        navigation,
        profile: {
            ...profile,
            user: {
                ...profile.user,
                timezone: get(timezone, 'data.data', DEFAULT_TIME_ZONE),
                isAdmin: !!intersectionWith(profile.user.roles, [
                    'ROLE_ADMIN',
                    'ROLE_SUPER',
                    'ROLE_ELMO_SUPER',
                ]).length,
                isManager: !!intersectionWith(profile.user.roles, ['ROLE_MANAGER']).length,
                isEmployee: !!intersectionWith(profile.user.roles, ['ROLE_EMPLOYEE']).length,
            },
        },
    };
};

export const redirectToSecureFrontend = () => {
    window.location.replace(
        `${
            process.env.NODE_ENV === 'development' ? process.env.REACT_APP_COBRA_URL : ''
        }/oauth2-client/redirect-to-secure-frontend/null?frontend-url=${window.location.href}`
    );
};

export const handleCheckTokenThenGetAppData = () =>
    authClient
        .get('/oauth2-client/auth/check-token')
        .then(async () => {
            const authData = await getAppData();
            return authData as TAuthData;
        })
        .catch((error) => {
            const err = error as AxiosError;

            if (
                typeof window !== 'undefined' &&
                err.response?.status &&
                err.response?.status === 401
            ) {
                redirectToSecureFrontend();
            }

            throw error;
        });

export const loginFn = () => handleCheckTokenThenGetAppData();

export const loadUser = () => handleCheckTokenThenGetAppData();

export const authKey = 'auth-user';

const authConfig = {
    key: authKey,
    loadUser,
    loginFn,
    logoutFn: async () => {},
    registerFn: async () => null as unknown as Promise<TAuthData>,
    LoaderComponent: loaderComponent,
    ErrorComponent,
};

const { AuthProvider, useAuth } = initReactQueryAuth<TAuthData | undefined>(authConfig);

export { AuthProvider, useAuth };
